import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import type { GmOnline } from '../../api/types';
import { t } from '../../i18n/idioma';
import { MIN_BUSCA } from './Jogadores';

export type Secao = 'painel' | 'jogadores' | 'mundo' | 'eventos' | 'itens' | 'skins' | 'registro';
export type AcaoNoJogador = 'ir' | 'ficha' | 'puxar' | 'reviver';

interface Props {
  onFechar(): void;
  onSecao(s: Secao): void;
  onJogador(acao: AcaoNoJogador, j: GmOnline): void;
  onOculto(): void;
  /** "item <nome>": abre Itens já com a busca. */
  onItem(texto: string): void;
}

interface Opcao { grupo: string; rotulo: string; sub: string; fazer(): void; manter?: boolean }

/** Os comandos com jogador: o texto é o começo ("ir", "puxar", "ficha", "reviver") e o resto é o nome. */
const COM_JOGADOR: readonly { acao: AcaoNoJogador; chave: string }[] = [
  { acao: 'ir', chave: 'gm.x.pal.ir' },
  { acao: 'puxar', chave: 'gm.x.pal.puxar' },
  { acao: 'ficha', chave: 'gm.x.pal.ficha' },
  { acao: 'reviver', chave: 'gm.x.pal.reviver' }
];

const SECOES: readonly { s: Secao; chave: string }[] = [
  { s: 'mundo', chave: 'gm.x.pal.hora' },
  { s: 'mundo', chave: 'gm.x.pal.invocar' },
  { s: 'itens', chave: 'gm.x.pal.item' },
  { s: 'eventos', chave: 'gm.x.pal.eventos' },
  { s: 'skins', chave: 'gm.x.pal.skins' },
  { s: 'registro', chave: 'gm.x.pal.registro' },
  { s: 'painel', chave: 'gm.x.pal.painel' }
];

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * A busca do GM (Ctrl+K): ações e jogadores pelo teclado. O jogador vem da mesma busca do servidor da lista (até 6),
 * nunca de uma lista local do shard (techspec-gm-menu §06).
 */
export function PaletaGm({ onFechar, onSecao, onJogador, onOculto, onItem }: Props) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [jogadores, setJogadores] = useState<readonly GmOnline[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  // O comando com jogador: "ir mor" → ação ir, nome "mor".
  const comando = useMemo(() => {
    const nq = norm(q);
    for (const c of COM_JOGADOR) {
      const palavra = norm(t(c.chave)).split(' ')[0];
      if (nq.startsWith(palavra + ' ')) return { acao: c.acao, nome: nq.slice(palavra.length).trim() };
    }
    return null;
  }, [q]);
  // "item espada" → busca de item, não de jogador.
  const palavraItem = norm(t('gm.x.pal.itemCmd'));
  const itemBuscado = norm(q).startsWith(palavraItem + ' ') ? q.trim().slice(palavraItem.length).trim() : null;
  const nomeBuscado = itemBuscado != null ? '' : comando ? comando.nome : norm(q);

  useEffect(() => {
    if (nomeBuscado.length < MIN_BUSCA) { setJogadores([]); return; }
    let vivo = true;
    const id = window.setTimeout(() => {
      api.listGmOnline(nomeBuscado, 6).then(p => { if (vivo) setJogadores(p.itens); }).catch(() => { if (vivo) setJogadores([]); });
    }, 200);
    return () => { vivo = false; window.clearTimeout(id); };
  }, [nomeBuscado]);

  const opcoes: Opcao[] = [];
  const nq = norm(q);
  if (itemBuscado != null) {
    if (itemBuscado.length >= 2) opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: t('gm.x.pal.buscarItem', { q: itemBuscado }), sub: t('gm.x.nav.itens'), fazer: () => onItem(itemBuscado) });
  } else if (comando) {
    const rot = t(COM_JOGADOR.find(c => c.acao === comando.acao)!.chave);
    for (const j of jogadores) {
      if (j.you && comando.acao !== 'ficha') continue;
      opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: `${rot} ${j.name}`, sub: `${j.city} · ${t('mundo.canal', { n: j.canal ?? 0 })}`, fazer: () => onJogador(comando.acao, j) });
    }
  } else {
    for (const c of COM_JOGADOR) {
      const rot = t(c.chave);
      if (!nq || norm(rot).includes(nq)) {
        opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: `${rot}…`, sub: t('gm.x.pal.digiteNome'), manter: true,
          fazer: () => setQ(norm(rot).split(' ')[0] + ' ') });
      }
    }
    for (const s of SECOES) {
      const rot = t(s.chave);
      if (!nq || norm(rot).includes(nq)) opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: rot, sub: '', fazer: () => onSecao(s.s) });
    }
    if (nq.length >= 2) opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: t('gm.x.pal.buscarItem', { q: q.trim() }), sub: t('gm.x.nav.itens'), fazer: () => onItem(q.trim()) });
    const oc = t('gm.x.pal.oculto');
    if (!nq || norm(oc).includes(nq)) opcoes.push({ grupo: t('gm.x.pal.acoes'), rotulo: oc, sub: 'Ctrl+H', fazer: onOculto });
    for (const j of jogadores) {
      opcoes.push({ grupo: t('gm.x.jogadores'), rotulo: j.name, sub: `${j.city} · ${t('mundo.canal', { n: j.canal ?? 0 })}`, fazer: () => onJogador('ficha', j) });
    }
  }
  const atual = Math.min(sel, Math.max(0, opcoes.length - 1));

  const executar = (o: Opcao | undefined) => {
    if (!o) return;
    o.fazer();
    if (!o.manter) onFechar();
    else { setSel(0); inputRef.current?.focus(); }
  };

  let grupo = '';
  return (
    <div className="gmx-veu" onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="gmx-paleta" role="dialog" aria-label={t('gm.x.buscarAcao')}>
        <input ref={inputRef} value={q} placeholder={t('gm.x.pal.placeholder')} autoComplete="off" aria-label={t('gm.x.buscarAcao')}
          onChange={e => { setQ(e.target.value); setSel(0); }}
          onKeyDown={e => {
            if (e.key === 'Escape') { e.stopPropagation(); onFechar(); }
            if (e.key === 'ArrowDown') { setSel(s => Math.min(opcoes.length - 1, s + 1)); e.preventDefault(); }
            if (e.key === 'ArrowUp') { setSel(s => Math.max(0, s - 1)); e.preventDefault(); }
            if (e.key === 'Enter') { e.preventDefault(); executar(opcoes[atual]); }
          }} />
        <ul role="listbox">
          {opcoes.length === 0 && <li><span className="gmx-meta">{nomeBuscado.length >= MIN_BUSCA ? t('gm.x.ninguemComEsseNome') : t('gm.x.pal.nada')}</span></li>}
          {opcoes.map((o, i) => {
            const cab = o.grupo !== grupo ? <span className="gmx-grupo">{(grupo = o.grupo)}</span> : null;
            return (
              <li key={`${o.grupo}-${o.rotulo}`} role="option" aria-selected={i === atual} onMouseEnter={() => setSel(i)} onClick={() => executar(o)}>
                {cab}
                <span>{o.rotulo}</span><small>{o.sub}</small>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
